'use client'

import { cn } from '@/lib/utils/index'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { forwardRef } from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full focus-visible:outline-none disabled:cursor-not-allowed  [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-criteria uppercase text-sm disabled:bg-neutral-300 disabled:!text-neutral-600 relative transition-all align-text-bottom',
  {
    variants: {
      variant: {
        default:
          'bg-neutral-700 hover:bg-neutral-800 active:bg-neutral-900 text-neutral-100',
        black:
          'bg-neutral-900 hover:bg-neutral-800 text-secondary-foreground active:bg-neutral-700 ',
        neutral:
          'bg-neutral-200 hover:bg-neutral-100 text-neutral-900 active:bg-neutral-100 ',
        primary:
          'bg-primary-500 hover:bg-primary-600 active:bg-primary-600 text-secondary-foreground',

        secondary:
          'bg-secondary hover:bg-secondary-400 active:bg-secondary-200 text-secondary-foreground',
        white:
          'bg-primary-foreground hover:bg-neutral-200 active:bg-secondary-600 text-neutral-900',
        outline:
          'border border-primary-500 bg-transparent hover:bg-primary-500 text-primary-500 hover:text-primary-foreground active:bg-primary-400 disabled:border-neutral-300 disabled:!text-neutral-600',
        'outline-neutral':
          'border border-neutral-900 bg-transparent hover:bg-neutral-900 text-neutral-900 hover:text-primary-foreground active:bg-transparent disabled:border-neutral-300 disabled:!text-neutral-600 disabled:bg-transparent',
        'outline-gray':
          'border border-neutral-300 bg-transparent text-neutral-700 hover:text-primary-foreground active:bg-transparent active:text-primary-500 hover:text-primary-500 disabled:border-neutral-300 disabled:!text-neutral-600 disabled:bg-transparent disabled:opacity-50',
        'outline-secondary':
          'border border-secondary bg-transparent hover:bg-secondary text-secondary hover:text-primary-foreground active:bg-secondary-400 disabled:border-neutral-300 disabled:!text-neutral-600',
        'outline-white':
          'border border-primary-foreground bg-transparent hover:bg-primary-foreground text-primary-foreground hover:text-foreground active:bg-primary-foreground disabled:border-neutral-300 disabled:!text-neutral-600',
        flat: 'py-2 px-0 text-primary-500 before:w-0 before:h-[1px] before:bg-primary-500 active:before:w-full hover:before:w-full  before:absolute before:bottom-0 before:left-0 before:transition-all before:duration-200 disabled:before:hidden disabled:bg-transparent disabled:!text-neutral-600',
        'flat-neutral':
          'py-2 px-0 text-neutral-900 before:w-0 before:h-[1px] before:bg-neutral-900 active:before:w-full hover:before:w-full  before:absolute before:bottom-0 before:left-0 before:transition-all before:duration-200 disabled:before:hidden disabled:bg-transparent disabled:!text-neutral-600',
        'flat-secondary':
          'py-2 px-0 text-orange-500 before:w-0 before:h-[1px] before:bg-orange-500 active:before:w-full hover:before:w-full  before:absolute before:bottom-0 before:left-0 before:transition-all before:duration-200 disabled:before:hidden disabled:bg-transparent disabled:!text-neutral-600',
        ghost:
          ' bg-transparent hover:bg-neutral-200 text-neutral-900 hover:text-primary-foreground  disabled:!text-neutral-600 disabled:bg-transparent',
        link: 'bg-transparent hover:bg-transparent text-primary-500 hover:text-primary-600 active:text-primary-700 disabled:text-primary-500',
      },
      size: {
        default: 'px-6 py-2 h-[37px]',
        small: 'px-3 py-1 normal-case text-sm h-[33px] gap-1',
        large: ' h-12 px-12 py-4 text-base',
        icon: 'h-10 w-10',
      },
      loading: {
        true: 'opacity-30 pointer-events-none cursor-progress',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const t = useTranslations()

    return loading ? (
      <Comp
        className={cn(buttonVariants({ variant, size, className, loading }))}
        ref={ref}
      >
        {t('button.processing')}... <Loader className='animate-spin' />
      </Comp>
    ) : (
      <Comp
        suppressHydrationWarning
        className={cn(buttonVariants({ variant, size, className, loading }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
